import { Outlet } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { Breadcrumb, type BreadcrumbItem } from '@/design-system';
import { ErrorBoundary } from '@/components/error-boundary';
import { useAuth } from '@/providers/auth-provider';
import { Sidebar } from './sidebar';
import { Topbar } from './topbar';
import { VendorAccessBanner } from './vendor-access-banner';

/**
 * Casca da aplicação.
 *
 * ACESSIBILIDADE — estrutura de marcos (landmarks):
 *   banner (header) · navigation (sidebar) · main · contentinfo
 * Quem usa leitor de tela navega por esses marcos em vez de tabular tudo. O
 * `<main id="conteudo-principal">` é o alvo do link "pular para o conteúdo",
 * que é o primeiro elemento focável da página (WCAG 2.4.1).
 */
export function AppShell() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { vendorAccess, organization } = useAuth();

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <a href="#conteudo-principal" className="qc-skip-link">
        Pular para o conteúdo principal
      </a>

      {/*
        AVISO PERMANENTE de acesso de fornecedor (plano §6.5).
        Fica no topo, sempre visível, e não pode ser dispensado: o operador
        precisa saber a todo momento que está dentro do ambiente de um cliente
        auditado e que cada ação sua gera um evento VENDOR_ACCESS na trilha.
      */}
      {vendorAccess ? <VendorAccessBanner organizationName={organization?.name ?? ''} /> : null}

      <Topbar onToggleNav={() => setMobileNavOpen((open) => !open)} navOpen={mobileNavOpen} />

      <div className="flex min-h-0 flex-1">
        <Sidebar open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

        <main
          id="conteudo-principal"
          tabIndex={-1}
          className="min-w-0 flex-1 focus:outline-none"
        >
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}

/**
 * Cabeçalho padrão de página: trilha, título, descrição e ações.
 *
 * O `<h1>` fica AQUI e em nenhum outro lugar da página. Manter um único h1 por
 * tela é o que permite ao leitor de tela responder "onde estou" com uma tecla.
 */
export function PageHeader({
  breadcrumbs,
  title,
  description,
  actions,
  children,
}: {
  readonly breadcrumbs?: readonly BreadcrumbItem[];
  readonly title: string;
  readonly description?: string;
  readonly actions?: ReactNode;
  readonly children?: ReactNode;
}) {
  return (
    <header className="border-b border-border bg-surface px-5 py-4">
      {breadcrumbs && breadcrumbs.length > 0 ? (
        <Breadcrumb items={breadcrumbs} className="mb-2" />
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-content">{title}</h1>
          {description ? (
            <p className="mt-1 max-w-3xl text-sm text-content-muted">{description}</p>
          ) : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>

      {children ? <div className="mt-4">{children}</div> : null}
    </header>
  );
}

/** Corpo padrão de página, com espaçamento consistente. */
export function PageBody({
  children,
  className,
}: {
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return <div className={`p-5 ${className ?? ''}`}>{children}</div>;
}
